import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import type { BrandVoiceProfile } from '@/lib/ai-agent/schemas/brand-voice-profile';
import { PANEL_CONTAINER_INSET, SECTION_LABEL, SECTION_TITLE } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

export type VoicePreviewContentType = 'instagram_caption' | 'linkedin_post' | 'product_blurb';

export interface VoicePreviewCardProps {
  draft: BrandVoiceProfile;
  withVoice: string;
  withoutVoice: string;
  contentType: VoicePreviewContentType;
  onSave: () => void;
  onEdit: () => void;
  isSaving?: boolean;
  className?: string;
}

const HUMAN_LABEL: Record<VoicePreviewContentType, string> = {
  instagram_caption: 'Instagram caption',
  linkedin_post: 'LinkedIn post',
  product_blurb: 'Product blurb',
};

export function VoicePreviewCard({
  draft,
  withVoice,
  withoutVoice,
  contentType,
  onSave,
  onEdit,
  isSaving = false,
  className,
}: VoicePreviewCardProps): JSX.Element {
  void draft;
  return (
    <div className={cn(PANEL_CONTAINER_INSET, 'p-5', className)}>
      <div className={cn(SECTION_TITLE, 'mb-4')}>Voice preview — {HUMAN_LABEL[contentType]}</div>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-lg bg-muted/40 p-4">
          <div className={cn(SECTION_LABEL, 'mb-2')}>WITHOUT your voice</div>
          <p className="text-body-chat text-foreground/70 whitespace-pre-wrap">{withoutVoice}</p>
        </div>
        <div className="rounded-lg bg-card p-4 border border-foreground/15">
          <div className={cn(SECTION_LABEL, 'mb-2')}>WITH your voice</div>
          <p className="text-body-chat text-foreground whitespace-pre-wrap">{withVoice}</p>
        </div>
      </div>
      <div className="mt-5 flex items-center justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onEdit} disabled={isSaving}>
          Edit
        </Button>
        <Button variant="neutral" size="sm" onClick={onSave} disabled={isSaving}>
          {isSaving ? (
            <span className="inline-flex items-center gap-1.5">
              <Spinner size="sm" />
              Saving
            </span>
          ) : (
            'Save'
          )}
        </Button>
      </div>
    </div>
  );
}
